import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, Plus } from "lucide-react";
import { notifications } from "@mantine/notifications";
import { Modal, TextInput, Select, NumberInput, FileInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { InlineLoader } from "../../../components/Loader/LoadingOverlay";
import { ButtonLoader } from "../../../components/Loader";

interface Deal {
  id: string;
  location: string;
  transaction_type: string;
  property_value: string;
  commission: string;
  property_type: string;
  date_created: string;
  status: string;
}

interface DealsResponse {
  results: Deal[];
  count: number;
  next?: string | null;
  previous?: string | null;
}

export default function Deals() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const form = useForm({
    initialValues: {
      location: "",
      transaction_type: "",
      property_value: "",
      commission: "",
      property_type: "",
      deal_type: "",
    },
    validate: {
      location: (value: string) => (!value.trim() ? "Location is required" : null),
      transaction_type: (value: string) =>
        !value.trim() ? "Transaction type is required" : null,
      property_value: (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === "") {
          return "Property value is required";
        }
        const strValue = String(value);
        if (!strValue.trim()) return "Property value is required";
        const numValue = parseFloat(strValue.replace(/[^\d.]/g, ""));
        if (isNaN(numValue) || numValue <= 0)
          return "Please enter a valid property value";
        return null;
      },
      commission: (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === "") {
          return "Commission is required";
        }
        const strValue = String(value);
        if (!strValue.trim()) return "Commission is required";
        const numValue = parseFloat(strValue.replace(/[^\d.]/g, ""));
        if (isNaN(numValue) || numValue <= 0)
          return "Please enter a valid commission";
        return null;
      },
      property_type: (value: string) =>
        !value.trim() ? "Property type is required" : null,
      deal_type: (value: string) =>
        !value.trim() ? "Deal type is required" : null,
    },
  });

  useEffect(() => {
    fetchDeals();
  }, []);

  async function fetchDeals() {
    setIsLoading(true);
    try {
      const endpoint = Endpoints.GetDeals();
      const response = await PerformRequest<DealsResponse>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const data = Array.isArray(response.data)
          ? { results: response.data, count: response.data.length }
          : response.data;
        setDeals(data.results || []);
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to load deals. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to load deals. Please try again.",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateDeal(values: typeof form.values) {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("location", values.location);
      formData.append("transaction_type", values.transaction_type);
      formData.append("property_value", String(values.property_value).replace(/[^\d.]/g, ""));
      formData.append("commission", String(values.commission).replace(/[^\d.]/g, ""));
      formData.append("property_type", values.property_type);
      formData.append("deal_type", values.deal_type);

      if (selectedFile) {
        formData.append("documents", selectedFile);
      }

      const endpoint = Endpoints.CreateDeal();
      const response = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: formData,
      });

      if (response.status === 200 || response.status === 201) {
        notifications.show({
          title: "Success",
          message: "Deal created successfully",
          color: "green",
        });
        setIsModalOpen(false);
        setSelectedFile(null);
        form.reset();
        fetchDeals();
      } else {
        notifications.show({
          title: "Error",
          message: response.message || "Failed to create deal. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.message || "Failed to create deal. Please try again.",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const transactionTypeOptions = [
    "Conveyancing & Assignment",
    "Sale Agreement",
    "Lease Agreement",
    "Mortgage",
    "Other",
  ];

  const propertyTypeOptions = [
    "Residential",
    "Commercial",
    "Industrial",
    "Mixed Use",
    "Land",
  ];

  const mockDeals = [
    {
      id: "7443ebf-2926-4d08-9270-952515c4aad0",
      location: "Mushin",
      transactionType: "Conveyancing & Assignment",
      propertyValue: "₦3,850,000.00",
      commission: "₦1,000,000.00",
      propertyType: "Commercial",
      dateCreated: "January 2, 2025",
      status: "APPROVED",
    },
    {
      id: "7443ebf-2926-4d08-9270-952515c4aad0",
      location: "Ibeju- Lekki",
      transactionType: "Conveyancing & Assignment",
      propertyValue: "₦3,850,000.00",
      commission: "₦1,000,000.00",
      propertyType: "Commercial",
      dateCreated: "January 2, 2025",
      status: "APPROVED",
    },
    {
      id: "7443ebf-2926-4d08-9270-952515c4aad0",
      location: "Apapa",
      transactionType: "Conveyancing & Assignment",
      propertyValue: "₦3,850,000.00",
      commission: "₦1,000,000.00",
      propertyType: "Commercial",
      dateCreated: "January 2, 2025",
      status: "APPROVED",
    },
    {
      id: "7443ebf-2926-4d08-9270-952515c4aad0",
      location: "Mushin",
      transactionType: "Conveyancing & Assignment",
      propertyValue: "₦3,850,000.00",
      commission: "₦1,000,000.00",
      propertyType: "Commercial",
      dateCreated: "January 2, 2025",
      status: "APPROVED",
    },
    {
      id: "7443ebf-2926-4d08-9270-952515c4aad0",
      location: "Alimosho",
      transactionType: "Conveyancing & Assignment",
      propertyValue: "₦3,850,000.00",
      commission: "₦1,000,000.00",
      propertyType: "Commercial",
      dateCreated: "January 2, 2025",
      status: "APPROVED",
    },
  ];

  return (
    <div className="dashboard-page" style={{ width: "100%" }}>
      <div className="dashboard-page__header">
        <button
          onClick={() => navigate("/d")}
          className="dashboard-page__back-button"
          type="button"
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
          <span>Deals</span>
        </button>
      </div>

      <section className="dashboard-panel shadow-[var(--card-shadow)] rounded-2xl" style={{ width: "100%" }}>
        <div className="dashboard-panel__header">
          <h2 className="dashboard-panel__title">All Deals</h2>
          <button
            onClick={() => setIsModalOpen(true)}
            className="dashboard-button"
            type="button"
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Plus size={16} />
            <span>Create New Deal</span>
          </button>
        </div>

        <div className="dashboard-deals-list">
          {isLoading ? (
            <InlineLoader isLoading={true} text="Loading deals..." />
          ) : deals.length === 0 ? (
            <div className="dashboard-empty">No deals found</div>
          ) : (
            deals.map((deal, index) => (
              <div key={`${deal.id}-${index}`} className="dashboard-deal-card">
                <div className="dashboard-deal-card__location">
                  {deal.location}
                </div>
                <div className="dashboard-deal-card__id">{deal.id}</div>
                <Link
                  to={`/d/deals/${deal.id}`}
                  className="dashboard-deal-card__action"
                >
                  <Eye size={16} aria-hidden="true" />
                  <span>View Details</span>
                </Link>
              </div>
            ))
          )}
        </div>

        <div className="dashboard-panel__footer">
          <button
            className="dashboard-button dashboard-button--secondary"
            type="button"
          >
            View More
          </button>
        </div>
      </section>

      <Modal
        opened={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedFile(null);
          form.reset();
        }}
        title="Create New Deal"
        size="lg"
      >
        <form
          onSubmit={form.onSubmit(handleCreateDeal)}
          className="dashboard-form"
        >
          <TextInput
            label="Location"
            placeholder="Enter property location"
            disabled={isSubmitting}
            error={form.errors.location}
            {...form.getInputProps("location")}
          />

          <Select
            label="Transaction Type"
            placeholder="Select transaction type"
            data={transactionTypeOptions}
            disabled={isSubmitting}
            error={form.errors.transaction_type}
            {...form.getInputProps("transaction_type")}
          />

          <div className="dashboard-form__grid">
            <NumberInput
              label="Property Value"
              placeholder="₦0.00"
              disabled={isSubmitting}
              error={form.errors.property_value}
              prefix="₦"
              thousandSeparator=","
              {...form.getInputProps("property_value")}
            />

            <NumberInput
              label="Commission"
              placeholder="₦0.00"
              disabled={isSubmitting}
              error={form.errors.commission}
              prefix="₦"
              thousandSeparator=","
              {...form.getInputProps("commission")}
            />
          </div>

          <Select
            label="Property Type"
            placeholder="Select property type"
            data={propertyTypeOptions}
            disabled={isSubmitting}
            error={form.errors.property_type}
            {...form.getInputProps("property_type")}
          />

          <Select
            label="Deal Type"
            placeholder="Select deal type"
            data={["mortgage", "sale", "lease", "other"]}
            disabled={isSubmitting}
            error={form.errors.deal_type}
            {...form.getInputProps("deal_type")}
          />

          <FileInput
            label="Documents (Optional)"
            placeholder="Upload property documents"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            disabled={isSubmitting}
            onChange={(file) => setSelectedFile(file)}
            value={selectedFile}
          />

          <div className="dashboard-form__actions" style={{ marginTop: "24px" }}>
            <button
              className="dashboard-button dashboard-button--secondary"
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                form.reset();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="dashboard-button flex items-center justify-center gap-2"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <ButtonLoader color="white" />
                  <span>Creating...</span>
                </>
              ) : (
                "Create Deal"
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

